import React, { useEffect, useMemo, useState } from "react";
import {
  Avatar,
  Box,
  Button,
  Chip,
  Grid,
  InputAdornment,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { PieChart, BarChart } from "@mui/x-charts";
import RefreshIcon from "@mui/icons-material/Refresh";
import DevicesIcon from "@mui/icons-material/Devices";
import CloudDoneIcon from "@mui/icons-material/CloudDone";
import Inventory2Icon from "@mui/icons-material/Inventory2";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import LaptopMacIcon from "@mui/icons-material/LaptopMac";
import MemoryIcon from "@mui/icons-material/Memory";
import StorageIcon from "@mui/icons-material/Storage";
import RouterIcon from "@mui/icons-material/Router";
import PrintIcon from "@mui/icons-material/Print";
import PhoneAndroidIcon from "@mui/icons-material/PhoneAndroid";
import DevicesOtherIcon from "@mui/icons-material/DevicesOther";
import AppsIcon from "@mui/icons-material/Apps";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import HourglassBottomIcon from "@mui/icons-material/HourglassBottom";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import SearchIcon from "@mui/icons-material/Search";
import { DevicesAPI } from "../api/devices";
import { AssetsAPI } from "../api/assets";
import { AnalyticsAPI } from "../api/analytics";
import { ReportsAPI } from "../api/reports";
import { listClouds } from "../api/clouds";
import { useNavigate } from "react-router-dom";
import { ROUTES } from "../routes";

const COLORS = {
  primary: "#1E3A8A",
  accent: "#0EA5E9",
  success: "#15803D",
  warning: "#C2410C",
  danger: "#B91C1C",
  muted: "#475569",
};

const fadeInUp = {
  "@keyframes pageFadeUp": {
    from: { opacity: 0, transform: "translateY(10px)" },
    to: { opacity: 1, transform: "translateY(0)" },
  },
};

const sectionMotion = delay => ({
  ...fadeInUp,
  opacity: 0,
  animation: `pageFadeUp 380ms ease ${delay}ms forwards`,
  "@media (prefers-reduced-motion: reduce)": {
    animation: "none",
    opacity: 1,
    transform: "none",
  },
});

function StatTile({ title, value, subtitle, icon, gradient, valueColor = "#0F172A", borderColor = "#E2E8F0", onClick, actionLabel }) {
  return (
    <Paper
      elevation={0}
      role={onClick ? "button" : undefined}
      tabIndex={onClick ? 0 : undefined}
      onClick={onClick}
      onKeyDown={(event) => {
        if (onClick && (event.key === "Enter" || event.key === " ")) {
          event.preventDefault();
          onClick();
        }
      }}
      sx={{
        p: 2,
        borderRadius: 2,
        background: gradient,
        color: "#0F172A",
        minHeight: 120,
        border: `1px solid ${borderColor}`,
        cursor: onClick ? "pointer" : "default",
        transition: "transform 160ms ease, box-shadow 160ms ease, border-color 160ms ease",
        "&:hover": onClick ? { transform: "translateY(-2px)", boxShadow: "0 10px 22px rgba(15,23,42,0.08)", borderColor: "#93C5FD" } : undefined,
        "&:focus-visible": onClick ? { outline: "3px solid #BFDBFE", outlineOffset: 2 } : undefined,
      }}
    >
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Box>
          <Typography variant="body2" sx={{ color: "#475569", fontWeight: 600 }}>
            {title}
          </Typography>
          <Typography variant="h3" fontWeight={900} sx={{ lineHeight: 1.05, mt: 0.4, color: valueColor, fontSize: { xs: "1.8rem", md: "2.1rem" } }}>
            {value}
          </Typography>
          <Typography variant="caption" sx={{ color: "#64748B" }}>
            {subtitle}
          </Typography>
          {actionLabel ? <Stack direction="row" alignItems="center" spacing={0.35} sx={{ mt: 0.9, color: COLORS.primary }}><Typography variant="caption" fontWeight={800}>{actionLabel}</Typography><ArrowForwardIcon sx={{ fontSize: 14 }} /></Stack> : null}
        </Box>
        <Avatar sx={{ bgcolor: "#EFF6FF", color: COLORS.primary, width: 42, height: 42 }}>
          {icon}
        </Avatar>
      </Stack>
    </Paper>
  );
}

function InsightTable({ title, subtitle, rows, columns, emptyText = "No data available." }) {
  const [query, setQuery] = useState("");
  const [page, setPage] = useState(0);
  const [orderBy, setOrderBy] = useState(columns[0]?.key || "");
  const [order, setOrder] = useState("desc");
  const pageSize = 5;
  const filtered = useMemo(() => {
    const needle = query.trim().toLowerCase();
    const source = needle
      ? rows.filter((row) => columns.some((column) => String(row[column.key] || "").toLowerCase().includes(needle)))
      : rows;
    return [...source].sort((left, right) => {
      const a = left[orderBy];
      const b = right[orderBy];
      const compare = typeof a === "number" || typeof b === "number"
        ? Number(a || 0) - Number(b || 0)
        : String(a || "").localeCompare(String(b || ""), undefined, { numeric: true, sensitivity: "base" });
      return order === "asc" ? compare : -compare;
    });
  }, [columns, order, orderBy, query, rows]);
  const safePage = Math.min(page, Math.max(0, Math.ceil(filtered.length / pageSize) - 1));
  const visibleRows = filtered.slice(safePage * pageSize, safePage * pageSize + pageSize);
  const sort = (key) => {
    if (orderBy === key) setOrder((value) => value === "asc" ? "desc" : "asc");
    else { setOrderBy(key); setOrder("asc"); }
  };

  return <Paper variant="outlined" sx={{ borderRadius: 2, borderColor: "#E2E8F0", overflow: "hidden", height: "100%" }}>
    <Box sx={{ p: { xs: 1.4, sm: 1.8 }, pb: 1.2, display: "flex", gap: 1.25, justifyContent: "space-between", alignItems: { xs: "stretch", sm: "center" }, flexDirection: { xs: "column", sm: "row" } }}>
      <Box><Typography variant="subtitle1" fontWeight={800}>{title}</Typography><Typography variant="caption" color="text.secondary">{subtitle}</Typography></Box>
      <TextField value={query} onChange={(event) => { setQuery(event.target.value); setPage(0); }} size="small" placeholder="Filter" sx={{ minWidth: { sm: 180 } }} InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }} />
    </Box>
    <TableContainer sx={{ maxHeight: 310 }}><Table size="small" stickyHeader sx={{ minWidth: 360 }}><TableHead><TableRow>{columns.map((column) => <TableCell key={column.key} sortDirection={orderBy === column.key ? order : false} sx={{ fontWeight: 800, bgcolor: "#F8FAFC", whiteSpace: "nowrap" }}><TableSortLabel active={orderBy === column.key} direction={orderBy === column.key ? order : "asc"} onClick={() => sort(column.key)}>{column.label}</TableSortLabel></TableCell>)}</TableRow></TableHead><TableBody>{visibleRows.length ? visibleRows.map((row, index) => <TableRow hover key={`${row[columns[0]?.key] || "row"}-${index}`}>{columns.map((column) => <TableCell key={column.key}>{column.render ? column.render(row[column.key], row) : row[column.key] ?? "—"}</TableCell>)}</TableRow>) : <TableRow><TableCell colSpan={columns.length} align="center" sx={{ py: 4, color: "text.secondary" }}>{emptyText}</TableCell></TableRow>}</TableBody></Table></TableContainer>
    <TablePagination component="div" count={filtered.length} page={safePage} onPageChange={(_, next) => setPage(next)} rowsPerPage={pageSize} rowsPerPageOptions={[pageSize]} labelRowsPerPage="Rows" />
  </Paper>;
}

function assetTypeBucket(type) {
  const v = String(type || "").toUpperCase();
  if (["DEVICE", "LAPTOP", "DESKTOP"].includes(v)) return "Computers";
  if (["RAM", "GPU", "CPU", "MOTHERBOARD"].includes(v)) return "Components";
  if (["SSD", "HDD", "STORAGE"].includes(v)) return "Storage";
  if (["ROUTER", "SWITCH", "FIREWALL", "NETWORK"].includes(v)) return "Networking";
  if (["PRINTER", "PERIPHERAL"].includes(v)) return "Peripherals";
  if (["MOBILE", "PHONE", "TABLET"].includes(v)) return "Mobiles";
  if (["LICENSE", "SOFTWARE"].includes(v)) return "Software";
  return "Other";
}

function bucketIcon(name) {
  switch (name) {
    case "Computers":
      return <LaptopMacIcon fontSize="small" />;
    case "Components":
      return <MemoryIcon fontSize="small" />;
    case "Storage":
      return <StorageIcon fontSize="small" />;
    case "Networking":
      return <RouterIcon fontSize="small" />;
    case "Peripherals":
      return <PrintIcon fontSize="small" />;
    case "Mobiles":
      return <PhoneAndroidIcon fontSize="small" />;
    case "Software":
      return <AppsIcon fontSize="small" />;
    default:
      return <DevicesOtherIcon fontSize="small" />;
  }
}

function normalizeCloudStatus(status) {
  return String(status || "").trim().toLowerCase();
}

function providerLabel(provider) {
  const p = String(provider || "").toLowerCase();
  if (p === "m365") return "Microsoft 365";
  if (p === "azure") return "Microsoft Azure";
  if (p === "aws") return "Amazon Web Services";
  return p || "Unknown";
}

function providerLogo(provider) {
  const p = String(provider || "").toLowerCase();
  if (p === "aws") return "/aws.png";
  if (p === "azure") return "/azure.png";
  if (p === "m365") return "/m365.jpg";
  return "";
}

function formatAuditDate(value) {
  if (!value) return "No completed audit yet";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "Date unavailable" : date.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
}

export default function EnterpriseDashboard() {
  const theme = useTheme();
  const navigate = useNavigate();
  const isSmall = useMediaQuery(theme.breakpoints.down("sm"));
  const chartWidth = isSmall ? 280 : 460;

  const [devices, setDevices] = useState([]);
  const [assets, setAssets] = useState([]);
  const [clouds, setClouds] = useState([]);
  const [softwareByVendor, setSoftwareByVendor] = useState([]);
  const [auditOverview, setAuditOverview] = useState(null);

  const loadDashboard = async () => {
    try {
      const [dRes, aRes, cRes, sRes, auditRes] = await Promise.allSettled([
        DevicesAPI.list(),
        AssetsAPI.list(),
        listClouds(),
        AnalyticsAPI.softwareSummary({ byVendor: true }),
        ReportsAPI.auditOverview(),
      ]);

      setDevices(dRes.status === "fulfilled" ? dRes.value?.data?.data || dRes.value?.data || [] : []);
      setAssets(aRes.status === "fulfilled" ? aRes.value?.data?.data || aRes.value?.data || [] : []);
      const cloudList = cRes.status === "fulfilled" ? (Array.isArray(cRes.value) ? cRes.value : cRes.value?.data || []) : [];
      setClouds(cloudList);
      setSoftwareByVendor(
        sRes.status === "fulfilled" && Array.isArray(sRes.value?.data?.byVendor)
          ? sRes.value.data.byVendor
          : []
      );
      setAuditOverview(auditRes.status === "fulfilled" ? auditRes.value?.data?.data || null : null);
    } catch (e) {
      console.error("dashboard load failed", e);
    }
  };

  useEffect(() => {
    void loadDashboard();
  }, []);

  const deviceStats = useMemo(() => {
    const total = devices.length;
    const active = devices.filter(d => {
      if (!d.last_seen) return false;
      const hours = (Date.now() - new Date(d.last_seen).getTime()) / (1000 * 60 * 60);
      return hours <= 24;
    }).length;
    return { total, active, inactive: total - active };
  }, [devices]);

  const assetStats = useMemo(() => {
    const total = assets.length;
    const assigned = assets.filter(a => !!a.device_id).length;
    const inStock = assets.filter(a => {
      const s = String(a.asset_status || "").toUpperCase();
      return s === "IN_STOCK" || s === "NOT_ALLOCATED";
    }).length;
    return { total, assigned, inStock };
  }, [assets]);

  const osData = useMemo(() => {
    const map = {};
    devices.forEach(d => {
      const os = d?.os_info?.distro || d?.os_info?.name || d?.os || "Unknown";
      map[os] = (map[os] || 0) + 1;
    });
    return Object.entries(map).map(([label, value], id) => ({ id, label, value }));
  }, [devices]);

  const topOsData = useMemo(() => osData.slice().sort((a, b) => b.value - a.value).slice(0, 6), [osData]);

  const typeBuckets = useMemo(() => {
    const map = {};
    assets.forEach(a => {
      const bucket = assetTypeBucket(a.asset_type);
      map[bucket] = (map[bucket] || 0) + 1;
    });
    return Object.entries(map)
      .sort((a, b) => b[1] - a[1])
      .map(([name, count]) => ({ name, count }));
  }, [assets]);

  const assetVendors = useMemo(() => {
    const map = {};
    assets.forEach(a => {
      const v = (a.vendor_name || "Unknown").trim() || "Unknown";
      map[v] = (map[v] || 0) + 1;
    });
    return Object.entries(map)
      .sort((a, b) => b[1] - a[1])
      .map(([name, value]) => ({ name, value }));
  }, [assets]);

  const topVendors = useMemo(() => assetVendors.slice(0, 8), [assetVendors]);

  const softwareVendors = useMemo(
    () =>
      softwareByVendor
        .slice()
        .sort((a, b) => (b.count || 0) - (a.count || 0))
        .map(v => ({ name: v.vendor || v.name || "Unknown", value: v.count || 0 })),
    [softwareByVendor]
  );

  const topSoftwareVendors = useMemo(() => softwareVendors.slice(0, 8), [softwareVendors]);

  const cloudPriority = useMemo(() => {
    const byProvider = { aws: null, azure: null, m365: null };
    clouds.forEach(c => {
      const key = String(c.provider || "").toLowerCase();
      if (byProvider[key] == null) byProvider[key] = c;
    });

    const providers = Object.entries(byProvider).map(([key, value]) => {
      if (!value) {
        return {
          key,
          name: providerLabel(key),
          state: "not_connected",
          label: "Not Connected",
          hint: "No tenant configured",
          color: "#64748B",
          bg: "#F8FAFC",
          logo: providerLogo(key),
          icon: <ErrorOutlineIcon fontSize="small" />,
        };
      }

      const status = normalizeCloudStatus(value.status);
      const hasSummary = !!value.summary;
      const isHealthy = status === "active" && hasSummary;

      if (isHealthy) {
        return {
          key,
          name: providerLabel(key),
          state: "healthy",
          label: "Connected",
          hint: value.lastCheckAt ? `Synced ${new Date(value.lastCheckAt).toLocaleString()}` : "Sync pending",
          color: "#166534",
          bg: "#ECFDF3",
          logo: providerLogo(key),
          icon: <CheckCircleOutlineIcon fontSize="small" />,
        };
      }

      if (status === "pending") {
        return {
          key,
          name: providerLabel(key),
          state: "pending",
          label: "Pending",
          hint: "Connection setup in progress",
          color: "#92400E",
          bg: "#FFFBEB",
          logo: providerLogo(key),
          icon: <HourglassBottomIcon fontSize="small" />,
        };
      }

      return {
        key,
        name: providerLabel(key),
        state: "attention",
        label: "Needs Attention",
        hint: "Authentication or sync issue",
        color: "#991B1B",
        bg: "#FEF2F2",
        logo: providerLogo(key),
        icon: <ErrorOutlineIcon fontSize="small" />,
      };
    });

    return {
      providers,
      connected: providers.filter(p => p.state === "healthy").length,
      total: providers.length,
    };
  }, [clouds]);

  const dashboardAudit = useMemo(() => {
    const summary = auditOverview?.latest_run_summary || {};
    return {
      runId: auditOverview?.latest_run?.id || null,
      status: String(auditOverview?.latest_run?.status || "NOT_STARTED").replace(/_/g, " "),
      isCompleted: String(auditOverview?.latest_run?.status || "").toLowerCase() === "completed",
      completedAt: auditOverview?.latest_run?.completed_at || null,
      startedAt: auditOverview?.latest_run?.started_at || null,
      coverage: Number(summary.coverage_pct || 0),
      scanned: Number(summary.scanned_assets || 0),
      tagged: Number(summary.tagged_assets || auditOverview?.tagged_assets || 0),
      exceptions: Number(summary.exceptions || 0),
    };
  }, [auditOverview]);

  const downloadExecutiveReport = async () => {
    try {
      const response = await ReportsAPI.downloadExecutivePdf();
      const url = URL.createObjectURL(new Blob([response.data], { type: "application/pdf" }));
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = `susha-executive-itam-report-${new Date().toISOString().slice(0, 10)}.pdf`;
      anchor.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Unable to download executive report", error);
    }
  };

  return (
    <Box sx={{ px: { xs: 1.25, sm: 1.75, md: 3 }, py: { xs: 1.5, sm: 2, md: 3 } }}>
      <Paper
        elevation={0}
        sx={{
          ...sectionMotion(0),
          p: { xs: 1.5, sm: 2, md: 2.5 },
          borderRadius: 2,
          bgcolor: "#FFFFFF",
          border: "1px solid #DBE4F0",
          mb: 2.5,
        }}
      >
        <Stack direction="row" alignItems="center" justifyContent="space-between" gap={{ xs: 1, sm: 2 }} flexWrap="wrap">
          <Box>
            <Typography variant="h5" fontWeight={800} color="#0F172A" sx={{ fontSize: { xs: "1.1rem", sm: "1.35rem", md: "1.55rem" } }}>
              Asset And Cloud Overview
            </Typography>
            <Typography variant="body2" color="#64748B" sx={{ fontSize: { xs: "0.78rem", sm: "0.88rem" } }}>
              Real-time enterprise visibility across assets, devices, software vendors, and cloud connections.
            </Typography>
          </Box>
          <Chip
            icon={<RefreshIcon />}
            label="Refresh Data"
            onClick={loadDashboard}
            sx={{
              bgcolor: "#F8FAFC",
              color: "#0F172A",
              border: "1px solid #CBD5E1",
              fontWeight: 700,
              fontSize: { xs: "0.72rem", sm: "0.8rem" },
            }}
          />
        </Stack>
      </Paper>

      <Grid data-tour="dashboard-stats" container spacing={{ xs: 1.25, sm: 1.6, md: 2 }} sx={{ ...sectionMotion(70), mb: 2.5 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatTile
            title="Total Assets"
            value={assetStats.total}
            subtitle="All tracked CIs"
            icon={<Inventory2Icon />}
            gradient="linear-gradient(180deg, #FFFFFF 0%, #F8FAFC 100%)"
            onClick={() => navigate(ROUTES.ASSETS)}
            actionLabel="View assets"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatTile
            title="Assigned Assets"
            value={assetStats.assigned}
            subtitle="Mapped to devices"
            icon={<CheckCircleIcon />}
            gradient="linear-gradient(180deg, #FFFFFF 0%, #F8FAFC 100%)"
            onClick={() => navigate(ROUTES.ASSETS)}
            actionLabel="Review assignment"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatTile
            title="Devices Online"
            value={deviceStats.active}
            subtitle="Seen in last 24 hours"
            icon={<CheckCircleIcon />}
            gradient="linear-gradient(180deg, #FFFFFF 0%, #ECFDF3 100%)"
            valueColor="#15803D"
            borderColor="#86EFAC"
            onClick={() => navigate(ROUTES.MONITOR)}
            actionLabel="View monitored devices"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatTile
            title="Devices Offline"
            value={deviceStats.inactive}
            subtitle="Needs connectivity check"
            icon={<DevicesIcon />}
            gradient="linear-gradient(180deg, #FFFFFF 0%, #FEF2F2 100%)"
            valueColor="#B91C1C"
            borderColor="#FECACA"
            onClick={() => navigate(ROUTES.MONITOR)}
            actionLabel="Investigate offline devices"
          />
        </Grid>
      </Grid>

      <Paper sx={{ ...sectionMotion(105), p: { xs: 1.4, sm: 1.8, md: 2 }, borderRadius: 2, border: "1px solid #DBE4F0", mb: 2.5, background: "linear-gradient(180deg, #FFFFFF 0%, #F8FBFF 100%)" }}>
        <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" alignItems={{ md: "center" }} gap={1.5}>
          <Box>
            <Typography variant="subtitle1" fontWeight={800}>Last QR Asset Audit & executive report</Typography>
            <Typography variant="body2" color="text.secondary">{dashboardAudit.runId ? (dashboardAudit.isCompleted ? `Last audit completed: ${formatAuditDate(dashboardAudit.completedAt)}. Review its coverage and exceptions below.` : `Latest audit started: ${formatAuditDate(dashboardAudit.startedAt)}. Review its current coverage and exceptions below.`) : "No audit has been completed yet. Start a QR asset audit when you are ready."}</Typography>
          </Box>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1} sx={{ width: { xs: "100%", md: "auto" }, flexShrink: 0 }}>
            <Button variant="outlined" onClick={downloadExecutiveReport} sx={{ whiteSpace: "nowrap", width: { xs: "100%", sm: "auto" } }}>Download executive report</Button>
            <Button variant="contained" onClick={() => navigate(ROUTES.REPORTS)} sx={{ whiteSpace: "nowrap", width: { xs: "100%", sm: "auto" } }}>{dashboardAudit.runId ? "View audit details" : "Start an audit"}</Button>
          </Stack>
        </Stack>
        <Grid container spacing={1.2} sx={{ mt: 0.8 }}>
          <Grid item xs={12} sm={6} lg={3}><Box sx={{ p: 1.25, minHeight: 72, border: "1px solid #E2E8F0", borderRadius: 1.5, bgcolor: "#fff" }}><Typography variant="caption" color="text.secondary">Last audit status</Typography><Typography variant="body2" fontWeight={800} sx={{ textTransform: "capitalize" }}>{dashboardAudit.status}</Typography></Box></Grid>
          <Grid item xs={12} sm={6} lg={3}><Box sx={{ p: 1.25, minHeight: 72, border: "1px solid #E2E8F0", borderRadius: 1.5, bgcolor: "#fff" }}><Typography variant="caption" color="text.secondary">Last audit coverage</Typography><Typography variant="body2" fontWeight={800}>{dashboardAudit.coverage}%</Typography></Box></Grid>
          <Grid item xs={12} sm={6} lg={3}><Box sx={{ p: 1.25, minHeight: 72, border: "1px solid #E2E8F0", borderRadius: 1.5, bgcolor: "#fff" }}><Typography variant="caption" color="text.secondary">Assets verified in last audit</Typography><Typography variant="body2" fontWeight={800}>{dashboardAudit.scanned}/{dashboardAudit.tagged}</Typography></Box></Grid>
          <Grid item xs={12} sm={6} lg={3}><Box sx={{ p: 1.25, minHeight: 72, border: "1px solid #E2E8F0", borderRadius: 1.5, bgcolor: "#fff" }}><Typography variant="caption" color="text.secondary">Last audit exceptions</Typography><Typography variant="body2" fontWeight={800} color={dashboardAudit.exceptions ? "error.main" : "text.primary"}>{dashboardAudit.exceptions}</Typography></Box></Grid>
        </Grid>
      </Paper>

      <Paper data-tour="dashboard-cloud-priority" sx={{ ...sectionMotion(130), p: { xs: 1.25, sm: 1.8, md: 2 }, borderRadius: 2, border: "1px solid #E5E7EB", mb: 2.5 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={1.5} flexWrap="wrap" gap={1}>
          <Typography variant="subtitle1" fontWeight={700}>
            Cloud Connection Priority
          </Typography>
          <Chip
            label={`${cloudPriority.connected}/${cloudPriority.total} Connected`}
            color={cloudPriority.connected > 0 ? "success" : "default"}
            variant="outlined"
            size="small"
          />
        </Stack>
        <Grid container spacing={{ xs: 1, sm: 1.3, md: 1.5 }}>
          {cloudPriority.providers.map(provider => (
            <Grid key={provider.key} item xs={12} md={4}>
              <Paper
                variant="outlined"
                sx={{
                  p: { xs: 1.1, sm: 1.35, md: 1.5 },
                  borderRadius: 1.5,
                  borderColor: "#E2E8F0",
                  bgcolor: provider.bg,
                }}
              >
                <Stack direction="row" alignItems="center" justifyContent="space-between" mb={0.5}>
                  <Stack direction="row" alignItems="center" spacing={1.1}>
                    {provider.logo ? (
                      <Box
                        component="img"
                        src={provider.logo}
                        alt={provider.name}
                        sx={{
                          width: 24,
                          height: 24,
                          objectFit: "contain",
                          filter: provider.state === "healthy" ? "none" : "grayscale(1)",
                          opacity: provider.state === "healthy" ? 1 : 0.35,
                        }}
                      />
                    ) : null}
                    <Typography variant="body2" fontWeight={700}>
                      {provider.name}
                    </Typography>
                  </Stack>
                  <Chip
                    icon={provider.icon}
                    label={provider.label}
                    size="small"
                    sx={{
                      bgcolor: "#FFFFFF",
                      color: provider.color,
                      border: "1px solid #E2E8F0",
                      "& .MuiChip-icon": { color: provider.color },
                    }}
                  />
                </Stack>
                <Typography variant="caption" color="text.secondary">
                  {provider.hint}
                </Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Paper>

      <Grid container spacing={{ xs: 1.25, sm: 1.6, md: 2 }} sx={sectionMotion(200)}>
        <Grid item xs={12} lg={5}>
          <Paper sx={{ p: { xs: 1.25, sm: 1.7, md: 2 }, borderRadius: 2, border: "1px solid #E5E7EB" }}>
            <Typography variant="subtitle1" fontWeight={700} mb={1}>
              Asset Categories
            </Typography>
            <Stack spacing={1}>
              {typeBuckets.length ? (
                typeBuckets.slice(0, 8).map(bucket => (
                  <Box
                    key={bucket.name}
                    sx={{
                      px: 1.2,
                      py: 0.8,
                      borderRadius: 1.5,
                      border: "1px solid #E2E8F0",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      bgcolor: "#F8FAFC",
                    }}
                  >
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Avatar sx={{ width: 28, height: 28, bgcolor: "#DBEAFE", color: COLORS.primary }}>
                        {bucketIcon(bucket.name)}
                      </Avatar>
                      <Typography variant="body2" fontWeight={600}>
                        {bucket.name}
                      </Typography>
                    </Stack>
                    <Chip label={bucket.count} size="small" sx={{ fontWeight: 700 }} />
                  </Box>
                ))
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No asset data available.
                </Typography>
              )}
            </Stack>
          </Paper>
        </Grid>

        <Grid item xs={12} lg={7}>
          <Paper sx={{ p: { xs: 1.25, sm: 1.7, md: 2 }, borderRadius: 2, border: "1px solid #E5E7EB" }}>
            <Typography variant="subtitle1" fontWeight={700} mb={1}>
              OS Distribution
            </Typography>
            <Typography variant="caption" color="text.secondary">Top 6 operating systems. See the complete list below.</Typography>
            <Box sx={{ display: "flex", justifyContent: "center" }}>
              <PieChart
                height={260}
                width={chartWidth}
                colors={[COLORS.primary, COLORS.accent, COLORS.success, COLORS.warning, COLORS.danger, COLORS.muted]}
                series={[{ data: topOsData.length ? topOsData : [{ id: 0, value: 1, label: "No Data" }], innerRadius: 48, outerRadius: 98 }]}
              />
            </Box>
          </Paper>
        </Grid>

        <Grid item xs={12} md={6}>
          <Paper sx={{ p: { xs: 1.25, sm: 1.7, md: 2 }, borderRadius: 2, border: "1px solid #E5E7EB" }}>
            <Typography variant="subtitle1" fontWeight={700} mb={1}>
              Top Asset Vendors
            </Typography>
            <Typography variant="caption" color="text.secondary">Top 8 vendors. See the complete list below.</Typography>
            {topVendors.length ? (
              <Box sx={{ overflowX: "auto" }}>
                <BarChart
                  height={280}
                  width={chartWidth}
                  xAxis={[{ scaleType: "band", data: topVendors.map(v => v.name) }]}
                  series={[{ data: topVendors.map(v => v.value), color: COLORS.primary }]}
                />
              </Box>
            ) : (
              <Paper
                variant="outlined"
                sx={{
                  p: 3,
                  borderStyle: "dashed",
                  borderColor: "#CBD5E1",
                  bgcolor: "#F8FAFC",
                  textAlign: "center",
                }}
              >
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  No vendors added yet.
                </Typography>
                <Button size="small" variant="contained" onClick={() => navigate(ROUTES.ASSETS)}>
                  Add your first vendor
                </Button>
              </Paper>
            )}
          </Paper>
        </Grid>

        <Grid item xs={12} md={6}>
          <Paper sx={{ p: { xs: 1.25, sm: 1.7, md: 2 }, borderRadius: 2, border: "1px solid #E5E7EB" }}>
            <Typography variant="subtitle1" fontWeight={700} mb={1}>
              Top Software Vendors
            </Typography>
            <Typography variant="caption" color="text.secondary">Top 8 publishers. See the complete list below.</Typography>
            <Box sx={{ overflowX: "auto" }}>
              <BarChart
                height={280}
                width={chartWidth}
                xAxis={[{ scaleType: "band", data: topSoftwareVendors.map(v => v.name) }]}
                series={[{ data: topSoftwareVendors.map(v => v.value), color: COLORS.success }]}
              />
            </Box>
          </Paper>
        </Grid>

        <Grid item xs={12}>
          <Paper sx={{ p: { xs: 1.25, sm: 1.7, md: 2 }, borderRadius: 2, border: "1px solid #E5E7EB" }}>
            <Typography variant="subtitle1" fontWeight={700} mb={1}>
              Cloud Connectivity
            </Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap">
              <Chip
                icon={<CloudDoneIcon />}
                label={`Total Connections: ${clouds.length}`}
                color="primary"
                variant="outlined"
              />
              <Chip
                label={`Healthy: ${cloudPriority.connected}`}
                color="success"
                variant="outlined"
              />
            </Stack>
          </Paper>
        </Grid>
      </Grid>

      <Box sx={{ ...sectionMotion(250), mt: 2.5 }}>
        <Box sx={{ mb: 1.5 }}>
          <Typography variant="subtitle1" fontWeight={800}>Detailed breakdowns</Typography>
          <Typography variant="body2" color="text.secondary">Charts show only the leading items to stay readable. Use these searchable, sortable tables for the complete result set.</Typography>
        </Box>
        <Grid container spacing={{ xs: 1.25, sm: 1.6, md: 2 }}>
          <Grid item xs={12} md={6}>
            <InsightTable title="Operating systems" subtitle="All monitored devices grouped by operating system." rows={osData.map(({ label, value }) => ({ operating_system: label, devices: value }))} columns={[{ key: "operating_system", label: "Operating system" }, { key: "devices", label: "Devices" }]} emptyText="No monitored devices yet." />
          </Grid>
          <Grid item xs={12} md={6}>
            <InsightTable title="Asset categories" subtitle="All active asset categories and their record counts." rows={typeBuckets.map((bucket) => ({ category: bucket.name, assets: bucket.count }))} columns={[{ key: "category", label: "Category" }, { key: "assets", label: "Assets" }]} emptyText="No assets recorded yet." />
          </Grid>
          <Grid item xs={12} md={6}>
            <InsightTable title="Asset vendors" subtitle="All vendors represented in the asset register." rows={assetVendors.map((vendor) => ({ vendor: vendor.name, assets: vendor.value }))} columns={[{ key: "vendor", label: "Vendor" }, { key: "assets", label: "Assets" }]} emptyText="No asset vendor data yet." />
          </Grid>
          <Grid item xs={12} md={6}>
            <InsightTable title="Software vendors" subtitle="Installed software grouped by publisher." rows={softwareVendors.map((vendor) => ({ vendor: vendor.name, installations: vendor.value }))} columns={[{ key: "vendor", label: "Publisher" }, { key: "installations", label: "Installations" }]} emptyText="No software inventory has been received yet." />
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
}
